import { AccountTypes, FilteredAccount } from '@interfaces/models';
import { createSelector } from '@ngrx/store';
import { getFormattedBrokerageAccounts } from '@shared/helpers';
import {
  selectEligibleInternalTransferFromAccounts,
  selectEligibleInternalTransferToAccounts,
} from '@store/selectors/client-accounts/client-accounts.selectors';
import {
  selectDivorceToggle,
  selectNonVCOrgTransferToggle,
  selectVCOrgToOrgToggle,
} from '@store/selectors/feature/feature.selectors';
import {
  selectInternalTransferFromAccountType,
  selectInternalTransferIsOwner,
  selectInternalTransferToAccountType,
} from '@store/selectors/internal-transfer/internal-transfer.selectors';

// For ORG to ORG: Must have ≥2 VC accounts OR ≥1 non-VC account
const hasValidOrgToOrgAccounts = (accounts: FilteredAccount[]): boolean => {
  const vcCount = accounts.filter((account) => account.isVanguardCharitable === true).length;
  const nonVcCount = accounts.length - vcCount;
  return vcCount >= 2 || nonVcCount >= 1;
};

export const selectInternalTransferEligibility = createSelector(
  selectEligibleInternalTransferFromAccounts,
  selectEligibleInternalTransferToAccounts,
  selectInternalTransferFromAccountType,
  selectInternalTransferToAccountType,
  selectInternalTransferIsOwner,
  selectDivorceToggle,
  selectVCOrgToOrgToggle,
  selectNonVCOrgTransferToggle,
  (
    eligibleInternalTransferFromAccounts,
    eligibleInternalTransferToAccounts,
    accountFromType,
    accountToType,
    isOwner,
    divorceToggle,
    vcOrgToOrgToggle,
    nonVcOrgTransferToggle,
  ) => {
    const allFromAccounts = eligibleInternalTransferFromAccounts.accounts;
    const allToAccounts = eligibleInternalTransferToAccounts.accounts;

    const isOrgToOrgTransfer =
      accountFromType === AccountTypes.ORG && accountToType === AccountTypes.ORG;
    const formattedFromAccounts = getFormattedBrokerageAccounts(allFromAccounts);
    const orgFlowEnabled = vcOrgToOrgToggle || nonVcOrgTransferToggle;

    if (orgFlowEnabled && isOrgToOrgTransfer && !hasValidOrgToOrgAccounts(formattedFromAccounts)) {
      return {
        isInternalTransferEligible: false,
        ineligibleFromAccountType: accountFromType,
        ineligibleToAccountType: accountToType,
      };
    }

    const hasEligibleFromAccounts: boolean = formattedFromAccounts.length >= 1;

    const canOpenNewAccount: boolean =
      (accountFromType === AccountTypes.INDV || accountFromType === AccountTypes.ESTATE) &&
      accountToType === AccountTypes.INDV;
    const isSameAccountTypesForFromAndTo: boolean = accountFromType === accountToType;

    let hasEligibleToAccounts: boolean = false;

    if (divorceToggle || !isOwner || canOpenNewAccount) {
      hasEligibleToAccounts = true;
    } else {
      const formattedAccountsLength: number = getFormattedBrokerageAccounts(allToAccounts).length;
      hasEligibleToAccounts = isSameAccountTypesForFromAndTo
        ? formattedAccountsLength >= 2
        : formattedAccountsLength >= 1;
    }

    // Determine eligibility
    const isInternalTransferEligible: boolean = hasEligibleFromAccounts && hasEligibleToAccounts;

    switch (true) {
      case isInternalTransferEligible:
        return {
          isInternalTransferEligible: true,
          fromAccounts: allFromAccounts,
          accountFromType,
        };
      case !hasEligibleToAccounts && !hasEligibleFromAccounts:
        return {
          isInternalTransferEligible: false,
          ineligibleToAccountType: accountToType,
          ineligibleFromAccountType: accountFromType,
        };
      case !hasEligibleToAccounts:
        return {
          isInternalTransferEligible: false,
          ineligibleToAccountType: accountToType,
        };
      case !hasEligibleFromAccounts:
        return {
          isInternalTransferEligible: false,
          ineligibleFromAccountType: accountFromType,
        };
      /* istanbul ignore next */
      default:
        throw new Error('UNREACHABLE');
    }
  },
);
